<template>
  <Teleport to="body">
    <Transition name="ca-modal">
      <div
        v-if="open"
        class="ca-app-modal__backdrop ds-scope"
        @click.self="$emit('close')"
      >
        <div
          class="ca-app-modal p-size-8"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ca-app-modal-title"
          @keydown.escape="$emit('close')"
          ref="dialogRef"
        >
          <button
            class="ca-app-modal__close"
            :aria-label="closeLabel"
            @click="$emit('close')"
          >
            <IconClose />
          </button>
          <div class="ca-app-modal_content gap-size-4">
            <h2
              id="ca-app-modal-title"
              class="ca-app-modal__title heading-regular"
            >

              {{ title }}
            </h2>
            <p class="ca-app-modal__subtitle paragraph-small">
              {{ subtitle }}
            </p>

            <div class="ca-app-modal__qr">
              <div class="ca-app-modal__qr-img">
                <img
                  :src="qrCode.src"
                  :alt="qrCode.alt || ''"
                  class="ca-app-modal__qr-img-src"
                />
              </div>
            </div>

            <div class="ca-app-modal__divider">

              <span class="ca-app-modal__divider-text caption-medium">{{ dividerText }}</span>
            </div>

            <div class="ca-app-modal__stores">
              <a
                :href="appStoreUrl"
                class="ca-app-modal__store-link"
                aria-label="Descarregar na App Store"
              >
                <IconAppStore class="ca-app-modal__store-badge" />
              </a>
              <a
                :href="playStoreUrl"
                class="ca-app-modal__store-link"
                aria-label="Descarregar no Google Play"
              >
                <IconPlayStore class="ca-app-modal__store-badge" />
              </a>
              <a
                :href="huaweiStoreUrl"
                class="ca-app-modal__store-link"
                aria-label="Explorar na AppGallery"
              >
                <IconHuaweiStore class="ca-app-modal__store-badge" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch, nextTick } from "vue";
import IconAppStore from "@assets/AppStore_Download_Btn-Dark-PT.svg";
import IconPlayStore from "@assets/GooglePlay_Download_Btn-dark-PT.svg";
import IconHuaweiStore from "@assets/Huawei_Download_Btn-Dark-PT.svg";
import IconClose from "@assets/Close_Icon-Button.svg";

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: "Instale a app CA",
  },
  subtitle: {
    type: String,
    default: "Leia o QR code para instalar a app",
  },
  dividerText: {
    type: String,
    default: "ou escolha a sua app store",
  },
  closeLabel: {
    type: String,
    default: "Fechar",
  },
  qrCode: {
    type: Object,
    default: null,
  },
  appStoreUrl: {
    type: String,
    default: "",
  },
  playStoreUrl: {
    type: String,
    default: "",
  },
  huaweiStoreUrl: {
    type: String,
    default: "",
  },
});

const appStoreUrl =
  "https://apps.apple.com/pt/app/cr%C3%A9dito-agr%C3%ADcola/id516082610";
const playStoreUrl =
  "https://play.google.com/store/apps/details?id=ca.mobile.explorer&hl=pt_PT";
const huaweiStoreUrl =
  "https://appgallery.huawei.com/app/C103173763?sharePrepath=ag&channelId=EUPTHMS202011CAMobile&detailType=0";

defineEmits(["close"]);

const dialogRef = ref(null);

watch(
  () => props.open,
  async (isOpen) => {
    if (isOpen) {
      await nextTick();
      dialogRef.value?.focus();
    }
  },
);
</script>

<style scoped lang="scss">
.ca-app-modal__backdrop {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
}

.ca-app-modal {
  position: relative;
  display: flex;
  min-width: 500px;
  max-width: 600px;
  max-height: 750px;
  flex-direction: column;
  align-items: center;
  border-radius: 24px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  outline: none;
}

.ca-app-modal_content {
  display: flex;
  width: 504px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
}

.ca-app-modal__close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  line-height: 1;
  border-radius: 50%;
  transition: background-color 0.15s ease;

  :deep(svg) {
    width: 48px;
    height: 48px;
    display: block;
  }

  &:hover {
    background-color: rgba(0, 0, 0, 0.06);
  }
}

.ca-app-modal__title {
  color: var(--ca-color-text-body);
  text-align: center;
  margin: 0 0 0px;
}

.ca-app-modal__subtitle {
  color: var(--ca-color-text-body-variant);
  text-align: center;
  margin: 0 0 0px;
}

.ca-app-modal__qr {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  align-self: stretch;
}

.ca-app-modal__qr-img {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 150px;
  height: 150px;
}

.ca-app-modal__qr-img-src {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.ca-app-modal__divider {
  padding: 8px 0px;
  width: 100%;
  display: flex;
  align-items: center;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 0;
    border-top: 0.5px solid var(--ca-color-text-light);
  }
}

.ca-app-modal__divider-text {
  color: var(--ca-color-text-body-variant);
  font-style: normal;
  letter-spacing: 0.168px;
  display: flex;
  min-height: 24px;
  padding: var(--size-3);
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  border-radius: 8px;
  border: 1px solid rgba(100, 111, 118, 0.1);
}

.ca-app-modal__stores {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  align-content: flex-start;
  gap: var(--size-4) var(--size-4);
  align-self: stretch;
  flex-wrap: wrap;
}

.ca-app-modal__store-link {
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
}

.ca-app-modal__store-badge {
  :deep(svg) {
    height: 48px;
    width: auto;
    display: block;
  }
}

// Transition
.ca-modal-enter-active,
.ca-modal-leave-active {
  transition: opacity 0.2s ease;
}

.ca-modal-enter-from,
.ca-modal-leave-to {
  opacity: 0;
}
</style>
 